<script lang="ts" setup>
import ListFilter from '@/components/common/ListFilter.vue';
import TranslatedState from '@/components/common/TranslatedState.vue';
import ContentItemForm from '@/components/content/ContentItemForm.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { Action, ActionType } from '@/models/action.model';
import { Hero } from '@/models/hero.model';
import { InfoGraphic } from '@/models/info-graphic.model';
import { getShortStatus, Initiative } from '@/models/initiative.model';
import { QuizQuestion } from '@/models/quiz-question.model';
import { TranslationState } from '@/models/translation.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { ContentStoreActions, ContentStoreState } from '@/stores/content.store';
import {
  TYPE_ACTION,
  TYPE_INFOGRAPHIC,
  NEW_ID,
  TYPE_INITIATIVE,
  TYPE_INITIATIVE_BY_CONTENT_EDITOR,
  TYPE_QUESTION,
} from '@/util/constants';
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();
const events = useEvents();
const router = useRouter();

export interface ContentItem {
  id: string;
  itemId: number;
  type: string;
  typeName: string;
  published: boolean;
  title: string;
  contentItem: Action | QuizQuestion | Hero | InfoGraphic;
  translated?: TranslationState;
}

const infoGraphics = computed<InfoGraphic[]>(
  () => store.state.cns.infoGraphics,
);
const actions = computed<Action[]>(() => store.state.cns.actions);
const initiatives = computed<Initiative[]>(() => store.state.cns.initiatives);
const questions = computed<QuizQuestion[]>(() => store.state.cns.questions);

const content = computed<ContentItem[]>(() => {
  return [
    ...infoGraphics.value.map((i) => {
      return {
        id: `${i.id}_infographic`,
        itemId: i.id,
        type: TYPE_INFOGRAPHIC,
        typeName: 'InfoGraphic',
        published: true,
        title: i.title,
        contentItem: i,
        status: '-',
        translated: i.translated,
      };
    }),
    ...initiatives.value.map((i) => {
      return {
        id: `${i.id}_initaitive`,
        itemId: i.id,
        type: TYPE_INITIATIVE,
        typeName: 'Initiative',
        title: i.title,
        published: i.published,
        contentItem: i,
        status: i.status,
        translated: i.translated,
      };
    }),
    ...actions.value.map((i) => {
      return {
        id: `${i.id}_action`,
        itemId: i.id,
        type: TYPE_ACTION,
        typeName: 'Action',
        title: i.title,
        published: i.published,
        contentItem: i,
        status: '-',
        translated: i.translated,
      };
    }),
    ...questions.value.map((i) => {
      return {
        id: `${i.id}_question`,
        itemId: i.id,
        type: TYPE_QUESTION,
        typeName: 'Question',
        title: i.title,
        published: true,
        contentItem: i,
        status: '-',
        translated: i.translated,
      };
    }),
  ].sort((a, b) => (a.title > b.title ? -1 : 1));
});

const update = () => {
  store.dispatch(ContentStoreActions.GetActions);
  store.dispatch(ContentStoreActions.GetInfoGraphics);
  store.dispatch(ContentStoreActions.GetInitiatives);
  store.dispatch(ContentStoreActions.GetQuizQuestions);
};

onMounted(() => {
  events.on(Event.UpdateContentItem, update);
  events.on(Event.LanguageChanged, update);
  update();
});

onBeforeUnmount(() => {
  events.off(Event.UpdateContentItem, update);
  events.off(Event.LanguageChanged, update);
});

const headers = [
  { title: 'ID', key: 'itemId', width: '5%' },
  { title: 'Type', key: 'typeName', width: '10%' },
  { title: 'Title', key: 'title', width: '40%' },
  { title: 'Category', key: 'categories', width: '25%' },
  { title: 'Status', key: 'status', width: '10%' },
  { title: 'State', key: 'translated', sortable: false },
  { title: 'Story', key: 'story', sortable: false, width: '5%' },
];

const editItem = (
  _: MouseEvent,
  rowData: { item: { type: string; contentItem: ContentItem } },
) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(ContentItemForm),
    data: {
      type: rowData.item.type,
      contentItem: rowData.item.contentItem,
    },
  });
};

const goToStory = async (event: MouseEvent, type: string, id: number) => {
  event.stopPropagation();
  event.preventDefault();
  const story = await store.dispatch(ContentStoreActions.GetStory, {
    type,
    id,
  });

  if (story.id) {
    router.push(`/stories/${story.id}`);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: `This ${type} is not connected to any story`,
      type: 'info',
    });
  }
};

const addItem = (type: string) => {
  const contentItem = {};
  switch (type) {
    case TYPE_INFOGRAPHIC:
      Object.assign(contentItem, {
        id: NEW_ID,
        title: '',
        description: '',
        media: [],
      });
      break;
    case TYPE_ACTION:
      Object.assign(contentItem, {
        id: NEW_ID,
        title: '',
        categories: [],
        steps: [],
        media: [],
        notRelevantForlabels: [],
        type: ActionType.Action,
        difficulty: 0,
        impact: 0,
      });
      break;
    case TYPE_INITIATIVE:
      Object.assign(contentItem, {
        id: NEW_ID,
        title: '',
        description: '',
        published: false,
        mainCategory: {},
        categories: [],
        contacts: [],
        website: '',
        address: {
          street: '',
          house_number: '',
          postal_code: '',
          city: '',
          country: 'NL',
          isSpecific: true,
          alternative: [],
        },
      });
      break;
  }

  store.commit(Mutations.ShowDrawer, {
    component: markRaw(ContentItemForm),
    data: {
      type: type,
      contentItem,
    },
  });
};

const filter = ref<string>('');
const filterTitle = ref<string>('');
const filteredContent = computed(() =>
  content.value
    .filter((v) =>
      filter.value
        ? filter.value === TYPE_INITIATIVE_BY_CONTENT_EDITOR
          ? (v.contentItem as Initiative).createdByUser
          : v.type === filter.value
        : true,
    )
    .filter((v) =>
      filterTitle.value
        ? `${v.title || ''}`
            .toLowerCase()
            .indexOf(filterTitle.value.toLowerCase()) !== -1
        : true,
    ),
);

function updateFilter(filters: { filter: string; filterTitle: string }) {
  filter.value = filters.filter;
  filterTitle.value = filters.filterTitle;
}
</script>
<template>
  <v-container>
    <h2>Content <CurrentLanguage /></h2>
    <ListFilter
      :options="[
        { label: 'Actions', value: TYPE_ACTION },
        { label: 'Infographics', value: TYPE_INFOGRAPHIC },
        { label: 'Initiatives', value: TYPE_INITIATIVE },
        { label: 'Content Editor', value: TYPE_INITIATIVE_BY_CONTENT_EDITOR },
        { label: 'Questions', value: TYPE_QUESTION },
      ]"
      @filter="updateFilter"
    />

    <v-data-table
      density="compact"
      :headers="headers"
      :items="filteredContent"
      @click:row.prevent="editItem"
      itemsPerPage="25"
      hover
    >
      <template v-slot:item.translated="{ item }">
        <div style="display: flex; flex-direction: row">
          <TranslatedState :item="item" />
          <v-tooltip text="This item is recommended">
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                icon="mdi-thumb-up-outline"
                color="primary"
                class="ml-2"
                v-if="!!(item.contentItem as Initiative).recommended"
              ></v-icon>
            </template>
          </v-tooltip>
          &nbsp;
          <v-tooltip
            :text="
              item.published
                ? 'This item is published'
                : 'This item is not published'
            "
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                :icon="item.published ? 'mdi-eye-check' : 'mdi-eye-outline'"
                :color="item.published ? 'green' : 'red'"
              ></v-icon>
            </template>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.categories="{ item }">
        {{
          (item.contentItem as any).mainCategory
            ? (item.contentItem as any).mainCategory.title
            : (item.contentItem as any).categories &&
              (item.contentItem as any).categories[0]
            ? (item.contentItem as any).categories[0].title
            : ''
        }}
      </template>
      <template v-slot:item.story="{ item }">
        <v-btn
          size="x-small"
          tiny
          @click.prevent="goToStory($event, item.type, item.contentItem.id)"
          >Story</v-btn
        >
      </template>
      <template v-slot:item.status="{ item, value }">
        <v-chip
          density="compact"
          variant="flat"
          color="primary"
          v-if="value !== '-'"
        >
          {{
            $t(
              `initiative.edit.status_${getShortStatus(
                item.contentItem as any,
                value,
              )}`,
            )
          }}
        </v-chip>
        <div v-else>{{ value }}</div>
      </template>
    </v-data-table>

    <v-menu>
      <template v-slot:activator="{ props }">
        <v-btn color="success" v-bind="props">Add Content</v-btn>
      </template>
      <v-list>
        <v-list-item>
          <v-list-item-title>
            <v-btn @click="() => addItem(TYPE_ACTION)" block variant="elevated"
              >Action</v-btn
            >
          </v-list-item-title>
        </v-list-item>
        <v-list-item>
          <v-list-item-title>
            <v-btn
              @click="() => addItem(TYPE_INITIATIVE)"
              block
              variant="elevated"
              >Initiative</v-btn
            >
          </v-list-item-title>
        </v-list-item>
        <v-list-item>
          <v-list-item-title>
            <v-btn
              @click="() => addItem(TYPE_INFOGRAPHIC)"
              block
              variant="elevated"
              >Infographic</v-btn
            >
          </v-list-item-title>
        </v-list-item>
        <!-- <v-list-item>
          <v-list-item-title>
            <v-btn @click="() => addItem(TYPE_HERO)" block variant="elevated"
              >Hero Page</v-btn
            >
          </v-list-item-title>
        </v-list-item> -->
      </v-list>
    </v-menu>
  </v-container>
</template>
